import { Cpu } from "@phosphor-icons/react"
import { LinkButton } from "@tokenterminal/core/ui/button/LinkButton"
import { BASE_PATH } from "@/constants/app.mjs"
import { productPageLogos } from "@/constants/productPageLogos"
import NicerBlockquote from "../../common/NicerBlockquote"
import ProductPageHeader from "../ProductPageHeader"
import ProductPageTemplate from "../ProductPageTemplate"
import {
  CustomerLogoStrip,
  FeatureCard,
  ProductCTASection,
  StatsGrid,
  ThreeFeaturesSection,
} from "../shared"
import { MCPChartDemo } from "./MCPChartDemo"
import { MCPChatDemo } from "./MCPChatDemo"
import { MCPConfigDemo } from "./MCPConfigDemo"

const MCPPage = () => {
  return (
    <ProductPageTemplate
      header={
        <ProductPageHeader
          icon={Cpu}
          title="MCP"
          backgroundImageUrl={`/${BASE_PATH}static/images/marketing/products/mcp/banner.png`}
          subtitle="Our MCP server connects Token Terminal's data directly to Claude and ChatGPT, enabling natural language queries on onchain metrics.
"
          ctaButtons={
            <>
              <LinkButton
                key="book-a-demo"
                size="large"
                variant="solid"
                href="https://calendly.com/d/cv8h-mgf-n3g/token-terminal-api-demo"
                style={{ zIndex: 4 }}
              >
                Book a demo
              </LinkButton>
              <LinkButton
                key="read-the-docs"
                size="large"
                variant="ghost"
                href="https://tokenterminal.com/docs/mcp/introduction"
                style={{ zIndex: 4 }}
              >
                Read the docs
              </LinkButton>
            </>
          }
          whoWeServeText="AI-native users and researchers who want to explore and analyze crypto data conversationally."
          simpleWhoWeServeText="AI-native users and researchers"
        />
      }
      productId="mcp"
    >
      {/* Customer Logo Strip */}
      <CustomerLogoStrip logos={productPageLogos.mcp} />

      {/* Three Features Section */}
      <ThreeFeaturesSection
        features={[
          {
            component: (
              <FeatureCard>
                <MCPChatDemo />
              </FeatureCard>
            ),
            title: "Query any metric in plain language",
            description: `Ask "What's Uniswap's trading volume this quarter?" and get accurate, sourced answers instantly.`,
          },
          {
            component: (
              <FeatureCard className="h-[300px]">
                <MCPConfigDemo />
              </FeatureCard>
            ),
            title: "Compare and analyze without code",
            description:
              "Request comparisons, trends, and rankings. Your AI assistant handles the data retrieval.",
          },
          {
            component: (
              <FeatureCard className="h-[300px]">
                <MCPChartDemo />
              </FeatureCard>
            ),
            title: "Charts and tables on demand",
            description:
              "Generate visualizations directly in your chat. Export to your workflow when ready.",
          },
        ]}
      />

      {/* Stats Grid */}
      <div className="mt-32">
        <StatsGrid
          stats={[
            {
              value: "100+",
              label: "Chains",
              logos: [
                { src: `${BASE_PATH}/static/images/marketing/logos/ethereum.png`, alt: "Ethereum" },
                { src: `${BASE_PATH}/static/images/marketing/logos/solana.png`, alt: "Solana" },
                { src: `${BASE_PATH}/static/images/marketing/logos/base.png`, alt: "Base" },
                { src: `${BASE_PATH}/static/images/marketing/logos/tron.png`, alt: "Tron" },
              ],
            },
            {
              value: "1,200+",
              label: "Apps",
              logos: [
                { src: `${BASE_PATH}/static/images/marketing/logos/aave.png`, alt: "Aave" },
                { src: `${BASE_PATH}/static/images/marketing/logos/uniswap.png`, alt: "Uniswap" },
                {
                  src: `${BASE_PATH}/static/images/marketing/logos/hyperliquid.png`,
                  alt: "Hyperliquid",
                },
                { src: `${BASE_PATH}/static/images/marketing/logos/sky.png`, alt: "Sky" },
              ],
            },
            {
              value: "7,000+",
              label: "Tokenized assets",
              logos: [
                { src: `${BASE_PATH}/static/images/marketing/logos/usdt.png`, alt: "USDT" },
                { src: `${BASE_PATH}/static/images/marketing/logos/usdc.png`, alt: "USDC" },
                { src: `${BASE_PATH}/static/images/marketing/logos/blackrock.png`, alt: "BUIDL" },
                {
                  src: `${BASE_PATH}/static/images/marketing/logos/robinhood.png`,
                  alt: "Robinhood",
                },
              ],
            },
            {
              value: "2+ PB",
              label: "Of data",
              hasSparkline: true,
            },
          ]}
        />
      </div>

      {/* Customer Quote */}
      <div className="max-w-[900px] mx-auto mt-32">
        <NicerBlockquote
          content="Token Terminal is the essential input to our real-time investing model. It's like building PE-grade models with streaming POS data."
          author="John McNiff"
          authorTitle="Co-founder at Theia"
          authorImage={`/${BASE_PATH}static/images/marketing/home/testimonials/avatar-john-theia.jpg`}
        />
      </div>

      {/* CTA Section */}
      <ProductCTASection
        tiers={[
          {
            id: "documentation",
            title: "Explore the documentation",
            features: [
              "Complete MCP tool reference",
              "Setup guides for Claude & Cursor",
              "Example queries and use cases",
              "API key management",
            ],
            cta: (
              <LinkButton
                className="w-full mt-4"
                size="large"
                href="https://tokenterminal.com/docs/mcp/introduction"
                target="_blank"
              >
                View documentation
              </LinkButton>
            ),
          },
          {
            id: "get-access",
            title: "Get MCP access",
            badge: { label: "PRO", color: "green" },
            features: [
              "MCP Server API key included",
              "Up to 20,000 tool calls per month",
              "Access to 500+ protocols and assets",
              "Real-time and historical data",
            ],
            cta: (
              <LinkButton className="w-full mt-4" size="large" href="/pricing" color="success">
                Subscribe to Pro
              </LinkButton>
            ),
          },
        ]}
      />
    </ProductPageTemplate>
  )
}

export default MCPPage
